/**
 * Queue Domain Constants
 */

// ============================================================================
// Loading Location Constants
// ============================================================================

export const LOCATION_KINDS = ['bay', 'silo'] as const

export type LocationKindType = (typeof LOCATION_KINDS)[number]

export const LOCATION_KIND_CONFIG: Record<
  LocationKindType,
  {
    label: string
    labelAr: string
    icon: string
    bgClass: string
    textClass: string
  }
> = {
  bay: {
    label: 'Bay',
    labelAr: 'الرصيف',
    icon: 'warehouse',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-600 dark:text-blue-400',
  },
  silo: {
    label: 'Silo',
    labelAr: 'الصومعة',
    icon: 'cylinder',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-600 dark:text-amber-400',
  },
}

// ============================================================================
// Queue Status Constants
// ============================================================================

export const QUEUE_STATUSES = [
  'waiting',
  'ready',
  'called',
  'entered',
  'loading',
  'completed',
  'cancelled',
  'expired',
  'no_show',
] as const

export type QueueStatusType = (typeof QUEUE_STATUSES)[number]

export const QUEUE_STATUS_CONFIG: Record<
  QueueStatusType,
  {
    label: string
    labelAr: string
    bgClass: string
    textClass: string
    dotClass: string
    isActive: boolean
    canCancel: boolean
    canEnter: boolean
  }
> = {
  waiting: {
    label: 'Waiting',
    labelAr: 'في الانتظار',
    bgClass: 'bg-gray-100 dark:bg-gray-800',
    textClass: 'text-gray-600 dark:text-gray-400',
    dotClass: 'bg-gray-500',
    isActive: true,
    canCancel: true,
    canEnter: false,
  },
  ready: {
    label: 'Ready',
    labelAr: 'جاهز',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-600 dark:text-green-400',
    dotClass: 'bg-green-500 animate-pulse',
    isActive: true,
    canCancel: true,
    canEnter: true,
  },
  called: {
    label: 'Called',
    labelAr: 'تم النداء',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-600 dark:text-blue-400',
    dotClass: 'bg-blue-500 animate-pulse',
    isActive: true,
    canCancel: true,
    canEnter: true,
  },
  entered: {
    label: 'Entered',
    labelAr: 'دخل المصنع',
    bgClass: 'bg-indigo-100 dark:bg-indigo-900/30',
    textClass: 'text-indigo-600 dark:text-indigo-400',
    dotClass: 'bg-indigo-500',
    isActive: true,
    canCancel: false,
    canEnter: false,
  },
  loading: {
    label: 'Loading',
    labelAr: 'جاري التحميل',
    bgClass: 'bg-purple-100 dark:bg-purple-900/30',
    textClass: 'text-purple-600 dark:text-purple-400',
    dotClass: 'bg-purple-500 animate-pulse',
    isActive: true,
    canCancel: false,
    canEnter: false,
  },
  completed: {
    label: 'Completed',
    labelAr: 'مكتمل',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-700 dark:text-green-300',
    dotClass: 'bg-green-600',
    isActive: false,
    canCancel: false,
    canEnter: false,
  },
  cancelled: {
    label: 'Cancelled',
    labelAr: 'ملغي',
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    textClass: 'text-red-600 dark:text-red-400',
    dotClass: 'bg-red-500',
    isActive: false,
    canCancel: false,
    canEnter: false,
  },
  expired: {
    label: 'Expired',
    labelAr: 'منتهي الصلاحية',
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    textClass: 'text-orange-600 dark:text-orange-400',
    dotClass: 'bg-orange-500',
    isActive: false,
    canCancel: false,
    canEnter: false,
  },
  no_show: {
    label: 'No Show',
    labelAr: 'لم يحضر',
    bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
    textClass: 'text-yellow-600 dark:text-yellow-400',
    dotClass: 'bg-yellow-500',
    isActive: false,
    canCancel: false,
    canEnter: false,
  },
}

export const ACTIVE_QUEUE_STATUSES: QueueStatusType[] = [
  'waiting',
  'ready',
  'called',
  'entered',
  'loading',
]

// ============================================================================
// Display-only Queue Status
// ============================================================================

/**
 * 'loaded' is a UI-only status. The backend has no such queue status — a truck
 * that finished loading keeps 'entered'/'loading' until it exits the factory,
 * which reads as "still being loaded" on a monitor. Derived from
 * `loadingCompletedAt` by `getQueueDisplayStatus`.
 *
 * Deliberately NOT added to QUEUE_STATUSES / QueueStatusType — those mirror the
 * API enum and feed status filters, so a fake value there would be sent to the
 * server.
 */
export type QueueDisplayStatusType = QueueStatusType | 'loaded'

export const QUEUE_DISPLAY_STATUS_CONFIG: Record<
  QueueDisplayStatusType,
  (typeof QUEUE_STATUS_CONFIG)[QueueStatusType]
> = {
  ...QUEUE_STATUS_CONFIG,
  loaded: {
    label: 'Loaded',
    labelAr: 'تم التحميل',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
    textClass: 'text-emerald-600 dark:text-emerald-400',
    dotClass: 'bg-emerald-500',
    isActive: true,
    canCancel: false,
    canEnter: false,
  },
}

// ============================================================================
// Form Field Constants
// ============================================================================

export const LOADING_LOCATION_FIELDS = {
  LOCATION_KIND: 'locationKind',
  LOCATION_NUMBER: 'locationNumber',
  LABEL: 'label',
  DESCRIPTION: 'description',
  IS_ACTIVE: 'isActive',
  DEVICE_ID: 'deviceId',
  VARIANT_ID: 'variantId',
  WINDOW_NUMBER: 'windowNumber',
  MONITOR_INDEX: 'monitorIndex',
  MAX_VEHICLES: 'maxVehicles',
  PRIORITY: 'priority',
  AVERAGE_LOADING_TIME: 'averageLoadingTime',
  FACTORY_ID: 'factoryId',
  TENANT_ID: 'tenantId',
} as const

export const QUEUE_ENTRY_FIELDS = {
  ORDER_ID: 'orderId',
  DRIVER_PHONE: 'driverPhone',
  DRIVER_NAME: 'driverName',
  VEHICLE_PLATE: 'vehiclePlate',
  VEHICLE_TYPE: 'vehicleType',
} as const

// ============================================================================
// Loading Location Rule Constants
// ============================================================================

export const LOADING_LOCATION_RULE_FIELDS = {
  LOADING_LOCATION_ID: 'loadingLocationId',
  CITY_ID: 'cityId',
  TENANT_ID: 'tenantId',
  PRIORITY: 'priority',
  IS_ACTIVE: 'isActive',
  DESCRIPTION: 'description',
} as const

// ============================================================================
// Translation Keys
// ============================================================================

export const T = {
  QUEUE: {
    // ── Moving a truck in the queue ──
    REORDER_TITLE: 'queue.reorderTitle',
    REORDER_DESCRIPTION: 'queue.reorderDescription',
    REORDER_MOVE_TO: 'queue.reorderMoveTo',
    REORDER_FRONT: 'queue.reorderFront',
    REORDER_BACK: 'queue.reorderBack',
    REORDER_AFTER: 'queue.reorderAfter',
    REORDER_AFTER_WHICH: 'queue.reorderAfterWhich',
    REORDER_PICK_TRUCK: 'queue.reorderPickTruck',
    REORDER_REASON: 'queue.reorderReason',
    REORDER_REASON_PLACEHOLDER: 'queue.reorderReasonPlaceholder',
    REORDER_REASON_HINT: 'queue.reorderReasonHint',
    REORDER_RECORDED_NOTICE: 'queue.reorderRecordedNotice',
    REORDER_CONFIRM: 'queue.reorderConfirm',
    REORDER_MOVING: 'queue.reorderMoving',
    REORDER_DONE: 'queue.reorderDone',

    // ── Queue simulation (read-only sandbox over the live yard) ──
    SIM_BASELINE: 'queue.simBaseline',
    SIM_ON_ITS_WAY: 'queue.simOnItsWay',
    SIM_IDLE: 'queue.simIdle',
    SIM_STAT_WAITING: 'queue.simStatWaiting',
    SIM_STAT_DONE: 'queue.simStatDone',
    SIM_STAT_WORST: 'queue.simStatWorst',
    SIM_MODE_LIVE: 'queue.simModeLive',
    SIM_MODE_REPLAY: 'queue.simModeReplay',
    SIM_MODE_BLANK: 'queue.simModeBlank',
    SIM_MODE_LIVE_HINT: 'queue.simModeLiveHint',
    SIM_MODE_REPLAY_HINT: 'queue.simModeReplayHint',
    SIM_MODE_BLANK_HINT: 'queue.simModeBlankHint',
    SIM_PICK_A_DAY: 'queue.simPickADay',
    SIM_DAY_TRUCKS: 'queue.simDayTrucks',
    SIM_NO_DAYS: 'queue.simNoDays',
    SIM_PLAY: 'queue.simPlay',
    SIM_PAUSE: 'queue.simPause',
    SIM_STEP: 'queue.simStep',
    SIM_ADD_TRUCKS: 'queue.simAddTrucks',
    SIM_ADD_N: 'queue.simAddN',
    SIM_START: 'queue.simStart',
    SIM_STARTING: 'queue.simStarting',
    SIM_HOW_IT_ENDED: 'queue.simHowItEnded',
    SIM_DAY_COMPLETE: 'queue.simDayComplete',
    SIM_THE_TRADE: 'queue.simTheTrade',
    SIM_TITLE: 'queue.simTitle',
    SIM_SUBTITLE: 'queue.simSubtitle',
    SIM_SUBTITLE_FACTORY: 'queue.simSubtitleFactory',
    SIM_READ_ONLY_NOTICE: 'queue.simReadOnlyNotice',
    SIM_BAYS: 'queue.simBays',
    SIM_NO_BAYS: 'queue.simNoBays',
    SIM_PRODUCT_LINES: 'queue.simProductLines',
    SIM_NO_ONE_WAITING: 'queue.simNoOneWaiting',
    SIM_WAITING_ANY_BAY: 'queue.simWaitingAnyBay',
    SIM_ALSO_TAKES: 'queue.simAlsoTakes',
    SIM_OPEN: 'queue.simOpen',
    SIM_CLOSED: 'queue.simClosed',
    SIM_NOT_LOADING: 'queue.simNotLoading',
    SIM_EMPTY_SLOT: 'queue.simEmptySlot',
    SIM_CALLED_EXPIRES: 'queue.simCalledExpires',
    SIM_BOUND_WAITING: 'queue.simBoundWaiting',
    SIM_SEND_AN_ORDER: 'queue.simSendAnOrder',
    SIM_SEARCH_ORDERS: 'queue.simSearchOrders',
    SIM_NO_ORDERS: 'queue.simNoOrders',
    SIM_NO_CUSTOMER: 'queue.simNoCustomer',
    SIM_TYPICAL_LOAD: 'queue.simTypicalLoad',
    SIM_SEND_IN: 'queue.simSendIn',
    SIM_PLUS_15: 'queue.simPlus15',
    SIM_PLUS_HOUR: 'queue.simPlusHour',
    SIM_UNDO: 'queue.simUndo',
    SIM_RESET_TO_LIVE: 'queue.simResetToLive',
    SIM_SO_FAR: 'queue.simSoFar',
    SIM_CHANGES_MADE: 'queue.simChangesMade',
    SIM_WHAT_HAPPENED: 'queue.simWhatHappened',
    SIM_NOTHING_YET: 'queue.simNothingYet',
    SIM_WHAT_THIS_ASSUMES: 'queue.simWhatThisAssumes',
    SIM_WHAT_CHANGES: 'queue.simWhatChanges',
    SIM_VS: 'queue.simVs',
    SIM_NO_MATERIAL_DIFFERENCE: 'queue.simNoMaterialDifference',
    SIM_COMPARE_TODAY_POOLED: 'queue.simCompareTodayPooled',
    SIM_COMPARE_TODAY_SMART: 'queue.simCompareTodaySmart',
    SIM_COMPARE_ALL: 'queue.simCompareAll',
    SIM_COMPARE_TODAY_ONLY: 'queue.simCompareTodayOnly',
    SIM_METRIC_SERVED: 'queue.simMetricServed',
    SIM_METRIC_WAITING: 'queue.simMetricWaiting',
    SIM_METRIC_NO_SHOW: 'queue.simMetricNoShow',
    SIM_METRIC_MESSAGES: 'queue.simMetricMessages',
    SIM_METRIC_MEDIAN_WAIT: 'queue.simMetricMedianWait',
    SIM_METRIC_P90_WAIT: 'queue.simMetricP90Wait',
    SIM_METRIC_WORST_WAIT: 'queue.simMetricWorstWait',
    SIM_METRIC_IDLE_BAY: 'queue.simMetricIdleBay',
    SIM_METRIC_OVERTAKES: 'queue.simMetricOvertakes',
    SIM_METRIC_PRE_EXISTING: 'queue.simMetricPreExisting',
    SIM_WAIT_EXPLAINER: 'queue.simWaitExplainer',
    TITLE: 'queue.title',
    LOADING_LOCATIONS: 'queue.loadingLocations',
    LOADING_LOCATION: 'queue.loadingLocation',
    QUEUE_ENTRIES: 'queue.queueEntries',
    QUEUE_ENTRY: 'queue.queueEntry',
    DASHBOARD: 'queue.dashboard',
    NEW_LOCATION: 'queue.newLocation',
    EDIT_LOCATION: 'queue.editLocation',
    DELETE_LOCATION: 'queue.deleteLocation',
    JOIN_QUEUE: 'queue.joinQueue',
    VALIDATE_ENTRY: 'queue.validateEntry',
    EXIT_FACTORY: 'queue.exitFactory',
    REFRESH_QUEUE: 'queue.refreshQueue',
    VIEW_QUEUE: 'queue.viewQueue',
    CANCEL_ENTRY: 'queue.cancelEntry',
    // Location fields
    LOCATION_KIND: 'queue.locationKind',
    LOCATION_NUMBER: 'queue.locationNumber',
    DEVICE_ID: 'queue.deviceId',
    WINDOW_NUMBER: 'queue.windowNumber',
    MONITOR_INDEX: 'queue.monitorIndex',
    MAX_VEHICLES: 'queue.maxVehicles',
    CURRENT_OCCUPANCY: 'queue.currentOccupancy',
    AVERAGE_LOADING_TIME: 'queue.averageLoadingTime',
    // Queue fields
    QUEUE_NUMBER: 'queue.queueNumber',
    DISPLAY_NUMBER: 'queue.displayNumber',
    QUEUE_STATUS: 'queue.queueStatus',
    POSITION: 'queue.position',
    ESTIMATED_WAIT: 'queue.estimatedWait',
    DRIVER_PHONE: 'queue.driverPhone',
    DRIVER_NAME: 'queue.driverName',
    VEHICLE_PLATE: 'queue.vehiclePlate',
    VEHICLE_TYPE: 'queue.vehicleType',
    // Stats
    AVAILABLE_SLOTS: 'queue.availableSlots',
    TOTAL_IN_QUEUE: 'queue.totalInQueue',
    WAITING_COUNT: 'queue.waitingCount',
    INSIDE_COUNT: 'queue.insideCount',
    // Messages
    ENTRY_ALLOWED: 'queue.entryAllowed',
    ENTRY_DENIED: 'queue.entryDenied',
    TICKET_GENERATED: 'queue.ticketGenerated',
    QUEUE_JOINED: 'queue.queueJoined',
    EXIT_RECORDED: 'queue.exitRecorded',
    // Validations
    REQUIRED_ORDER: 'queue.validation.requiredOrder',
    REQUIRED_DEVICE_ID: 'queue.validation.requiredDeviceId',
    REQUIRED_VARIANT: 'queue.validation.requiredVariant',
    REQUIRED_MAX_VEHICLES: 'queue.validation.requiredMaxVehicles',
    UNIQUE_DEVICE_ID: 'queue.validation.uniqueDeviceId',
    // Loading Location Rules
    LOADING_LOCATION_RULES: 'queue.loadingLocationRules',
    LOADING_LOCATION_RULE: 'queue.loadingLocationRule',
    RULE_LOADING_LOCATION: 'queue.ruleLoadingLocation',
    RULE_CITY: 'queue.ruleCity',
    RULE_PRIORITY: 'queue.rulePriority',
    RULE_DESCRIPTION: 'queue.ruleDescription',
    ADD_RULE: 'queue.addRule',
    EDIT_RULE: 'queue.editRule',
    DELETE_RULE: 'queue.deleteRule',
    // Rule form
    RULE_FORM_SECTION: 'queue.ruleForm.section',
    RULE_FORM_LOADING_LOCATION: 'queue.ruleForm.loadingLocation',
    RULE_FORM_SELECT_LOADING_LOCATION: 'queue.ruleForm.selectLoadingLocation',
    RULE_FORM_CITY: 'queue.ruleForm.city',
    RULE_FORM_SELECT_CITY: 'queue.ruleForm.selectCity',
    RULE_FORM_TENANT: 'queue.ruleForm.tenant',
    RULE_FORM_SELECT_TENANT: 'queue.ruleForm.selectTenant',
    RULE_FORM_PRIORITY: 'queue.ruleForm.priority',
    RULE_FORM_DESCRIPTION: 'queue.ruleForm.description',
    RULE_FORM_DESCRIPTION_PLACEHOLDER: 'queue.ruleForm.descriptionPlaceholder',
    RULE_FORM_IS_ACTIVE: 'queue.ruleForm.isActive',
    RULE_FORM_IS_ACTIVE_HELP: 'queue.ruleForm.isActiveHelp',
    // Rule messages
    RULE_CREATED: 'queue.messages.ruleCreated',
    RULE_UPDATED: 'queue.messages.ruleUpdated',
    RULE_DELETED: 'queue.messages.ruleDeleted',
    // Rule errors
    RULE_CREATE_FAILED: 'queue.errors.createRuleFailed',
    RULE_UPDATE_FAILED: 'queue.errors.updateRuleFailed',
    RULE_DELETE_FAILED: 'queue.errors.deleteRuleFailed',
    RULE_FETCH_FAILED: 'queue.errors.fetchRulesFailed',
    // Rule filters
    RULE_FILTER_LOCATION: 'queue.ruleFilter.location',
    RULE_FILTER_CITY: 'queue.ruleFilter.city',
    // Rule empty
    RULE_EMPTY_TITLE: 'queue.empty.noRules',
    RULE_EMPTY_DESC: 'queue.empty.noRulesDescription',
  },
  COMMON: {
    SAVE: 'common.save',
    CANCEL: 'common.cancel',
    DELETE: 'common.delete',
    EDIT: 'common.edit',
    VIEW: 'common.view',
    LOADING: 'common.loading',
    ACTIVE: 'common.active',
    INACTIVE: 'common.inactive',
    ALL: 'common.all',
    SEARCH: 'common.search',
    NO_RESULTS: 'common.noResults',
  },
} as const

// ============================================================================
// Event Type Constants
// ============================================================================

export const EVENT_TYPES = [
  'queue_joined',
  'status_changed',
  'notification_sent',
  'entry_validated',
  'entered_factory',
  'entered_loading',
  'exited_factory',
  'slot_released',
  'cancelled',
] as const

export type EventTypeValue = (typeof EVENT_TYPES)[number]

export const EVENT_TYPE_CONFIG: Record<
  EventTypeValue,
  {
    label: string
    labelAr: string
    icon: string
    color: string
  }
> = {
  queue_joined: {
    label: 'Joined Queue',
    labelAr: 'انضم للطابور',
    icon: 'user-plus',
    color: 'blue',
  },
  status_changed: {
    label: 'Status Changed',
    labelAr: 'تغيير الحالة',
    icon: 'refresh-cw',
    color: 'gray',
  },
  notification_sent: {
    label: 'Notification Sent',
    labelAr: 'إرسال إشعار',
    icon: 'bell',
    color: 'amber',
  },
  entry_validated: {
    label: 'Entry Validated',
    labelAr: 'التحقق من الدخول',
    icon: 'shield-check',
    color: 'green',
  },
  entered_factory: {
    label: 'Entered Factory',
    labelAr: 'دخول المصنع',
    icon: 'log-in',
    color: 'indigo',
  },
  entered_loading: {
    label: 'At Loading Point',
    labelAr: 'في نقطة التحميل',
    icon: 'truck',
    color: 'purple',
  },
  exited_factory: {
    label: 'Exited Factory',
    labelAr: 'خروج من المصنع',
    icon: 'log-out',
    color: 'slate',
  },
  slot_released: {
    label: 'Slot Released',
    labelAr: 'تحرير المكان',
    icon: 'unlock',
    color: 'teal',
  },
  cancelled: {
    label: 'Cancelled',
    labelAr: 'ملغي',
    icon: 'x-circle',
    color: 'red',
  },
}

// ============================================================================
// API Endpoints
// ============================================================================

export const API_ENDPOINTS = {
  LOADING_LOCATIONS: '/api/loading-locations',
  LOADING_LOCATION_BY_ID: (id: string) => `/api/loading-locations/${id}`,
  LOADING_LOCATION_QUEUE: (id: string) => `/api/loading-locations/${id}/queue`,
  LOADING_LOCATION_STATS: (id: string) => `/api/loading-locations/${id}/stats`,
  LOADING_LOCATION_TIMELINE: (id: string) => `/api/loading-locations/${id}/timeline`,
  LOADING_LOCATIONS_DASHBOARD: '/api/loading-locations/dashboard',
  LOADING_LOCATIONS_BY_VARIANT: (variantId: string) =>
    `/api/loading-locations/by-variant/${variantId}`,

  QUEUE_ENTRIES: '/api/queue-entries',
  QUEUE_ENTRY_BY_ID: (id: string) => `/api/queue-entries/${id}`,
  QUEUE_JOIN: '/api/queue-entries/join',
  QUEUE_MY_ENTRIES: '/api/queue-entries/my-entries',
  QUEUE_CANCEL: (id: string) => `/api/queue-entries/${id}/cancel`,
  QUEUE_REORDER: (id: string) => `/api/queue-entries/${id}/reorder`,
  QUEUE_REFRESH: (locationId: string) =>
    `/api/queue-entries/refresh/${locationId}`,

  QUEUE_EVENTS: '/api/queue-events',
  QUEUE_EVENTS_BY_ENTRY: (entryId: string) =>
    `/api/queue-events/by-entry/${entryId}`,
  QUEUE_EVENTS_RECENT: '/api/queue-events/recent',

  QUEUE_SIMULATION_SEED: '/api/queue-simulation/seed',
  QUEUE_SIMULATION_ORDERS: '/api/queue-simulation/orders',
  QUEUE_SIMULATION_RUN: '/api/queue-simulation/run',
  QUEUE_SIMULATION_DAYS: '/api/queue-simulation/days',
  QUEUE_SIMULATION_REPLAY: '/api/queue-simulation/replay',

  LOADING_LOCATION_RULES: '/api/loading-location-rules',
  LOADING_LOCATION_RULE_BY_ID: (id: string) => `/api/loading-location-rules/${id}`,
} as const
